import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { uploadFile } from "../../../helpers/API";
import { collateralTypeConfig } from "../formConfig";

/**
 * Custom hook that encapsulates all collateral-related state and handlers.
 */
const useCollaterals = () => {
  const { t } = useTranslation();

  const [collaterals, setCollaterals] = useState([]);
  const [showCollateralModal, setShowCollateralModal] = useState(false);
  const [editingCollateralId, setEditingCollateralId] = useState(null);
  const [collateralForm, setCollateralForm] = useState({ type: "" });

  // Photo upload states
  const [collateralPhotoUploading, setCollateralPhotoUploading] = useState(false);
  const [collateralPhotoPreview, setCollateralPhotoPreview] = useState(null);

  const handleCollateralFormChange = (field, value) => {
    setCollateralForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCollateralTypeChange = (type) => {
    const newForm = { type };
    if (collateralTypeConfig[type]) {
      collateralTypeConfig[type].fields.forEach((field) => {
        newForm[field.name] = field.type === "file" ? null : "";
      });
    }
    setCollateralForm(newForm);
  };

  const handleAddCollateral = () => {
    if (!collateralForm.type) {
      toast.error(t("customer.add.validation.pleaseFillCollateralFields"));
      return;
    }
    const config = collateralTypeConfig[collateralForm.type];
    if (config) {
      const missingRequired = config.fields.some(
        (field) => field.required && !collateralForm[field.name]
      );
      if (missingRequired) {
        toast.error(t("customer.add.validation.pleaseFillCollateralFields"));
        return;
      }
    }
    if (editingCollateralId) {
      setCollaterals((prev) =>
        prev.map((c) => (c.id === editingCollateralId ? { ...c, ...collateralForm } : c))
      );
      setEditingCollateralId(null);
    } else {
      const collateral = { id: Date.now(), ...collateralForm };
      setCollaterals((prev) => [...prev, collateral]);
    }
    setCollateralForm({ type: "" });
    setShowCollateralModal(false);
    toast.success(t("customer.add.messages.saveCollateralSuccess"));
  };

  const handleEditCollateral = (collateral) => {
    const formData = { ...collateral };
    delete formData.id;
    setCollateralForm(formData);
    setCollateralPhotoPreview(collateral.photo || null);
    setEditingCollateralId(collateral.id);
    setShowCollateralModal(true);
  };

  const handleRemoveCollateral = (id) => {
    setCollaterals((prev) => prev.filter((c) => c.id !== id));
  };

  const handleCollateralPhotoUpload = async (file) => {
    if (!file) return;
    setCollateralPhotoUploading(true);
    setCollateralPhotoPreview(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await uploadFile(formData);
      handleCollateralFormChange("photo", res?.data?.url || res?.url || res?.data || "");
      toast.success(t("customer.add.messages.photoUploadSuccess"));
    } catch (error) {
      console.error("Collateral photo upload error:", error);
      toast.error(t("customer.add.messages.photoUploadError"));
      setCollateralPhotoPreview(null);
      handleCollateralFormChange("photo", null);
    } finally {
      setCollateralPhotoUploading(false);
    }
  };

  const closeCollateralModal = () => {
    setShowCollateralModal(false);
    setEditingCollateralId(null);
    setCollateralPhotoPreview(null);
  };

  const openAddCollateralModal = () => {
    setCollateralForm({ type: "" });
    setEditingCollateralId(null);
    setCollateralPhotoPreview(null);
    setShowCollateralModal(true);
  };

  return {
    collaterals,
    setCollaterals,
    showCollateralModal,
    editingCollateralId,
    collateralForm,
    collateralPhotoUploading,
    collateralPhotoPreview,
    handleCollateralFormChange,
    handleCollateralTypeChange,
    handleAddCollateral,
    handleEditCollateral,
    handleRemoveCollateral,
    handleCollateralPhotoUpload,
    closeCollateralModal,
    openAddCollateralModal,
  };
};

export default useCollaterals;
